import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  FAILURE_TYPES,
  listInstalledParts,
  listQuarantineBins,
  recordInstalledPartFailure,
} from '@/lib/api/warranty.js';
import type { TicketRecord } from '@/lib/api/tickets.js';
import { listUsers } from '@/lib/api/users.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { WarrantyBadge } from '@/components/tickets/WarrantyBadge.js';

export function WarrantyTicketBanner({ ticket }: { ticket: TicketRecord }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [partId, setPartId] = useState('');
  const [failureType, setFailureType] = useState('early_failure');
  const [reason, setReason] = useState('');
  const [binId, setBinId] = useState('');
  const [attributedTo, setAttributedTo] = useState('');
  const children = ticket.warrantyTickets ?? [];
  const show = ticket.isWarranty || children.length > 0;
  if (!show) return null;

  const partsQuery = useQuery({
    queryKey: ['installed-parts', ticket.id],
    queryFn: () => listInstalledParts(ticket.id),
    enabled: open,
  });
  const binsQuery = useQuery({
    queryKey: ['quarantine-bins'],
    queryFn: listQuarantineBins,
    enabled: open,
  });
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
    enabled: open && failureType === 'tech_damage',
  });

  const mutation = useMutation({
    mutationFn: () =>
      recordInstalledPartFailure(ticket.id, {
        installedTicketPartId: partId,
        failureType,
        failureReason: reason || undefined,
        quarantineBinId: binId || undefined,
        disposition: 'quarantine',
        attributedTo: failureType === 'tech_damage' ? attributedTo || undefined : undefined,
      }),
    onSuccess: () => {
      toastSuccess('Failed part moved to quarantine');
      setOpen(false);
      setPartId('');
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  return (
    <div className="space-y-3">
      {ticket.isWarranty && ticket.parentTicket ? (
        <div className="rounded-2xl border border-violet-300 bg-violet-50 px-4 py-3 text-sm text-violet-950 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <WarrantyBadge isWarranty />
                <p className="font-semibold">
                  Warranty rework of{' '}
                  <Link className="underline" to={`/tickets/${ticket.parentTicket.id}`}>
                    {ticket.parentTicket.ticketNumber}
                  </Link>
                </p>
              </div>
              {ticket.warrantyReason ? <p className="mt-1 text-violet-900">{ticket.warrantyReason}</p> : null}
              <p className="mt-1 text-xs text-violet-800">
                Warranty work copied from the original is $0. Anything you add after this is billed normally.
                {ticket.warrantyExpiresAt
                  ? ` Coverage still ends ${new Date(ticket.warrantyExpiresAt).toLocaleDateString()}.`
                  : ' This ticket keeps the original warranty window.'}
              </p>
            </div>
            <button
              type="button"
              className="rounded-xl bg-violet-700 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-800"
              onClick={() => setOpen(true)}
            >
              Record failed part
            </button>
          </div>
        </div>
      ) : null}

      {children.length > 0 ? (
        <div className="rounded-2xl border border-violet-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm">
          <p className="font-semibold text-violet-900">
            {children.length} warranty ticket{children.length === 1 ? '' : 's'} opened against this
          </p>
          <ul className="mt-2 space-y-1">
            {children.map((child) => (
              <li key={child.id}>
                <Link className="font-semibold text-primary hover:underline" to={`/tickets/${child.id}`}>
                  {child.ticketNumber}
                </Link>
                <span className="text-slate-500"> · {child.status}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <form
            className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-2xl"
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
          >
            <h3 className="text-lg font-semibold text-slate-950">Record failed part</h3>
            <p className="text-sm text-slate-600">
              Pick a part installed on {ticket.parentTicket?.ticketNumber ?? 'the original ticket'}. It leaves
              sellable stock and waits in quarantine.
            </p>
            <label className="block text-sm font-medium text-slate-700">
              Installed part
              <select
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={partId}
                onChange={(event) => setPartId(event.target.value)}
                required
              >
                <option value="">Select…</option>
                {(partsQuery.data ?? []).map((part) => (
                  <option key={part.id} value={part.id}>
                    {part.partName}
                    {part.supplierName ? ` · ${part.supplierName}` : ''} · ${part.unitCost}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Failure type
              <select
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={failureType}
                onChange={(event) => setFailureType(event.target.value)}
              >
                {FAILURE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
            {failureType === 'tech_damage' ? (
              <label className="block text-sm font-medium text-slate-700">
                Attributed technician
                <select
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={attributedTo}
                  onChange={(event) => setAttributedTo(event.target.value)}
                >
                  <option value="">Unknown</option>
                  {(usersQuery.data ?? []).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="block text-sm font-medium text-slate-700">
              Quarantine bin
              <select
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={binId}
                onChange={(event) => setBinId(event.target.value)}
              >
                <option value="">Unassigned bin</option>
                {(binsQuery.data ?? []).filter((bin) => bin.active).map((bin) => (
                  <option key={bin.id} value={bin.id}>
                    {bin.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              What happened
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={!partId || mutation.isPending}
                className="rounded-xl bg-violet-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {mutation.isPending ? 'Saving…' : 'Move to quarantine'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
